/** Core domain model. Deliberately independent of Google Drive so that the mock
 *  source used in demo mode and the real Drive source are interchangeable. */

export interface Track {
  /** Drive file id, or a synthetic id in demo mode. */
  id: string;
  /** Display name with the extension and any leading track number stripped. */
  name: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  /** Unknown until the file is read: Drive does not report audio length.
   *  Learned by playing or measuring, and remembered (lib/durations.ts). */
  durationSec?: number;
}

export interface Chapter {
  title: string;
  /** Index of the track this chapter lives in. */
  trackIndex: number;
  /** Offset from the start of that track, in seconds. */
  startSec: number;
}

export type BookKind = 'folder' | 'single';

/** A folder that groups books, e.g. "Top 100 books" or an author's folder. */
export interface FolderRef {
  id: string;
  name: string;
}

export interface Book {
  /** Drive folder id for multi-file books, file id for single-file books. */
  id: string;
  title: string;
  author?: string;
  /** Resolved cover image URL, if one was found. */
  coverUrl?: string;
  kind: BookKind;
  tracks: Track[];
  totalSizeBytes: number;
  /** The grouping folders between the library root and this book, outermost
   *  first. Never includes the book's own folder. Absent for books at the root
   *  — and for libraries cached before folders were tracked, which therefore
   *  show flat until the next refresh. */
  folderPath?: FolderRef[];
}

export interface BookProgress {
  trackIndex: number;
  positionSec: number;
  /** Total duration of the whole book, once known. 0 while still unknown. */
  durationSec: number;
  /** ISO 8601. The merge key for cross-device sync. */
  updatedAt: string;
  /** Human-readable label of the device that last wrote this entry. */
  device: string;
  finished: boolean;
}

export interface ProgressFile {
  version: 1;
  books: Record<string, BookProgress>;
}

export const EMPTY_PROGRESS: ProgressFile = { version: 1, books: {} };

/**
 * Everything the app needs from a library backend. `mock.ts` and the real
 * Drive source both implement this, which is what lets the entire UI, player
 * and sync loop be exercised without a Google account.
 */
export interface LibrarySource {
  readonly kind: 'demo' | 'drive';
  /** Label shown in the header, e.g. "Demo library" or the Drive folder name. */
  readonly label: string;

  listBooks(): Promise<Book[]>;

  /** A URL the <audio> element can load directly. May be async: the Drive
   *  source falls back to buffering a Blob when no service worker is
   *  available to proxy range requests. */
  trackUrl(track: Track): string | Promise<string>;

  /** A URL from which a track's length can be read by loading only its header,
   *  or null when that cannot be done cheaply right now. */
  metadataUrl?(track: Track): string | null;

  /** Reads an inclusive byte range of a track — how chapters and cover art
   *  are read out of a file without downloading it. */
  readRange?(track: Track, start: number, end: number): Promise<ArrayBuffer>;

  /** Chapters for a book, if the source can determine them. */
  chaptersFor(book: Book): Promise<Chapter[]>;

  loadProgress(): Promise<ProgressFile | null>;
  saveProgress(progress: ProgressFile): Promise<void>;
}
